import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { formatMoney, currencySymbol, timeAgo, offerEventLine } from "@/lib/format";
import { PageLoader, EmptyState, ErrorState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, HandCoins, Check, X, Repeat } from "lucide-react";

const badge = { pending: "bg-amber-100 text-amber-700", accepted: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-600", countered: "bg-sky-100 text-sky-700", expired: "bg-gray-100 text-gray-500" };

export default function Offers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [counterFor, setCounterFor] = useState(null);
  const [amt, setAmt] = useState("");
  const [msg, setMsg] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["listing-offers", id], queryFn: () => apiFetch(`/listings/${id}/offers`) });

  const act = async (fn, m) => {
    try { await fn(); toast.success(m); qc.invalidateQueries({ queryKey: ["listing-offers", id] }); }
    catch (err) { toast.error(err.message); }
  };

  const submitCounter = async () => {
    await act(() => apiFetch(`/offers/${counterFor.id}/counter`, { method: "POST", body: JSON.stringify({ amount: Number(amt), message: msg.trim() }) }), "Counter sent");
    setCounterFor(null); setAmt(""); setMsg("");
  };

  if (isLoading) return <PageLoader />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const { listing, offers } = data;
  const cur = listing.currency;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full p-1.5 hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-xl font-extrabold">Offers</h1>
          <p className="text-sm text-muted-foreground">{listing.title} · {formatMoney(listing.price, cur)}</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {offers.length === 0 ? (
          <EmptyState icon={HandCoins} title="No offers yet" message="Buyers' offers will appear here." />
        ) : (
          offers.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border p-4" data-testid={`offer-${o.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{o.buyer_name}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${badge[o.status] || "bg-secondary"}`}>{o.status}</span>
              </div>
              <p className="mt-1 text-2xl font-extrabold text-price">{formatMoney(o.amount, cur)}</p>
              {o.status === "countered" && <p className="text-sm text-sky-700">You countered: {formatMoney(o.counter_amount, cur)}</p>}
              {o.message && <p className="mt-1 text-sm text-muted-foreground">"{o.message}"</p>}
              {o.history?.length > 0 && (
                <div className="mt-2 space-y-0.5 border-l-2 border-border pl-3 text-xs text-muted-foreground">
                  {o.history.map((h, i) => <div key={i}>{offerEventLine(h, cur)} · {timeAgo(h.at)}</div>)}
                </div>
              )}
              {o.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => act(() => apiFetch(`/offers/${o.id}/decision`, { method: "POST", body: JSON.stringify({ accept: true }) }), "Offer accepted")} className="flex items-center gap-1 rounded-lg bg-price px-3 py-1.5 text-xs font-semibold text-white" data-testid={`accept-offer-${o.id}`}><Check className="h-3.5 w-3.5" />Accept</button>
                  <button onClick={() => setCounterFor(o)} className="flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white" data-testid={`counter-offer-${o.id}`}><Repeat className="h-3.5 w-3.5" />Counter</button>
                  <button onClick={() => act(() => apiFetch(`/offers/${o.id}/decision`, { method: "POST", body: JSON.stringify({ accept: false }) }), "Offer declined")} className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold" data-testid={`decline-offer-${o.id}`}><X className="h-3.5 w-3.5" />Decline</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={!!counterFor} onOpenChange={(o) => !o && setCounterFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Counter offer</DialogTitle></DialogHeader>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currencySymbol(cur)}</span>
            <input data-testid="counter-amount" type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Counter amount" className="w-full rounded-xl border border-input py-2.5 pl-8 pr-4 text-sm outline-none focus:border-price" />
          </div>
          <textarea data-testid="counter-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Message (optional)" className="w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" />
          <DialogFooter><button onClick={submitCounter} disabled={!amt} className="rounded-xl bg-price px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="submit-counter">Send counter</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
